import { useEffect, useMemo, useState } from "react";

const Star = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    className={`h-9 w-9 md:h-11 md:w-11 ${filled ? "text-amber-400" : "text-slate-200"}`}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 17.3l-6.18 3.73 1.64-7.19L2 8.77l7.26-.63L12 1.5l2.74 6.64 7.26.63-5.46 5.07 1.64 7.19L12 17.3z" />
  </svg>
);

const StarRating = ({ label, value, onChange, required }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="text-sm md:text-base font-semibold text-slate-700">
          {label}
        </div>
        {required && <span className="text-xs text-rose-500">Required</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {stars.map((star) => (
          <button
            key={`${label}-${star}`}
            type="button"
            onClick={() => onChange(star)}
            className="rounded-full border border-transparent focus:outline-none focus:ring-2 focus:ring-rose-300"
            aria-label={`${label} ${star} stars`}
          >
            <Star filled={value >= star} />
          </button>
        ))}
      </div>
    </div>
  );
};

const defaultRatings = {
  overall: 0,
  salon: 0,
  receptionist: 0,
  service: 0,
  staff: 0,
  online_booking: 0,
};

const criterionKeyToValue = {
  salon: "salon",
  receptionist: "receptionist",
  service: "service",
  staff: "staff",
  online_booking: "online_booking",
};

const formatServiceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parts = trimmed.split("-");
    if (parts.length === 3) {
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
        const dateObj = new Date(year, month - 1, day);
        if (!Number.isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
      }
    }
  }
  return null;
};

export default function SurveyForm({
  context,
  onSubmit,
  submitLabel = "Submit feedback",
  resetKey,
}) {
  const [ratings, setRatings] = useState(defaultRatings);
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [publicReview, setPublicReview] = useState(null);

  useEffect(() => {
    setRatings(defaultRatings);
    setComment("");
    setSubmitError("");
    setSubmitting(false);
    setSubmitted(false);
    setPublicReview(null);
  }, [resetKey]);

  const config = context?.survey_config || {};
  const status = context?.status || "pending";
  const showOnlineBooking = Boolean(context?.show_online_booking_rating);
  const publicReviewConfig = config?.public_review || {};
  const allowPublicReview = publicReviewConfig?.enabled !== false;

  const criteria = useMemo(() => {
    const items = Array.isArray(config?.criteria) ? config.criteria : [];
    return items.filter((entry) => {
      if (!entry?.enabled) return false;
      if (entry?.key === "online_booking" && entry?.show_if === "booked_via_portal") {
        return showOnlineBooking;
      }
      return true;
    });
  }, [config?.criteria, showOnlineBooking]);

  const overallQuestion = config?.overall_question || "Rate your visit";
  const commentConfig = config?.comment || {};
  const commentEnabled = commentConfig?.enabled !== false;
  const commentThreshold = Number.isFinite(commentConfig?.required_if_overall_below)
    ? Number(commentConfig.required_if_overall_below)
    : 4;
  const commentRequired =
    commentEnabled && ratings.overall > 0 && ratings.overall < commentThreshold;

  const canSubmit = useMemo(() => {
    if (!ratings.overall) return false;
    const requiredCriteria = criteria.filter((entry) => entry?.required);
    for (const entry of requiredCriteria) {
      const key = entry?.key;
      const ratingKey = criterionKeyToValue[key];
      if (ratingKey && !ratings[ratingKey]) return false;
    }
    if (commentRequired && !comment.trim()) return false;
    return true;
  }, [criteria, ratings, commentRequired, comment]);

  const handleSubmit = async () => {
    if (!onSubmit || !canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        overall_rating: ratings.overall,
        salon_rating: ratings.salon || null,
        receptionist_rating: ratings.receptionist || null,
        service_rating: ratings.service || null,
        staff_rating: ratings.staff || null,
        online_booking_rating: showOnlineBooking ? ratings.online_booking || null : null,
        comment: comment.trim() || null,
      };
      const result = await onSubmit(payload);
      setPublicReview(result || null);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Unable to submit survey.");
    } finally {
      setSubmitting(false);
    }
  };

  const serviceDate = formatServiceDate(context?.service_date_local);

  const showThankYou = submitted;
  const showAlreadySubmitted = !submitted && status === "completed";
  const showExpired = !submitted && status === "expired";
  const showForm = !submitted && status === "pending";

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-1">
        <div className="text-sm text-slate-500">Salon</div>
        <div className="text-lg md:text-xl font-semibold text-slate-900">
          {context?.salon_name || "Your Salon"}
        </div>
        {serviceDate && (
          <div className="text-sm text-slate-500">Visit date: {serviceDate}</div>
        )}
      </div>

      {showThankYou && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-700">
          <div className="font-semibold text-emerald-700">
            {config?.thank_you?.title || "Thank you"}
          </div>
          <div className="text-sm text-emerald-700">
            {config?.thank_you?.message || "Thank you - your feedback was sent to the salon."}
          </div>
          {allowPublicReview &&
            publicReview?.show_public_review_prompt &&
            publicReview?.public_review_redirect_url && (
              <div className="mt-3 space-y-2">
                {config?.public_review?.helper_text && (
                  <div className="text-xs text-emerald-700">
                    {config.public_review.helper_text}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() =>
                    window.open(publicReview.public_review_redirect_url, "_blank")
                  }
                  className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {config?.public_review?.cta_text || "Leave a public review"}
                </button>
              </div>
            )}
        </div>
      )}

      {showAlreadySubmitted && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-slate-600">
          This survey has already been submitted.
        </div>
      )}

      {showExpired && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-slate-600">
          This survey link has expired. Please contact the salon if you need help.
        </div>
      )}

      {showForm && (
        <div className="space-y-6">
          {submitError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
              {submitError}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-6">
            <StarRating
              label={overallQuestion}
              value={ratings.overall}
              onChange={(value) => setRatings((prev) => ({ ...prev, overall: value }))}
              required
            />

            <div className="grid grid-cols-1 gap-5">
              {criteria.map((entry) => {
                const key = entry?.key;
                const ratingKey = criterionKeyToValue[key];
                if (!ratingKey) return null;
                return (
                  <StarRating
                    key={key}
                    label={entry?.label || key}
                    value={ratings[ratingKey]}
                    onChange={(value) =>
                      setRatings((prev) => ({ ...prev, [ratingKey]: value }))
                    }
                    required={!!entry?.required}
                  />
                );
              })}
            </div>

            {commentEnabled && (
              <div className="space-y-2">
                <label className="text-sm md:text-base font-semibold text-slate-700">
                  {commentConfig?.label || "Additional comments"}
                </label>
                {commentRequired && (
                  <div className="text-xs text-rose-600">
                    {commentConfig?.required_message ||
                      "We are sorry it wasn't perfect - could you share a quick note so we can improve?"}
                  </div>
                )}
                <textarea
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm md:text-base"
                  placeholder="Share any details that can help the salon."
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="rounded-full bg-slate-900 px-6 py-3 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : submitLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
